/**
 * User preferences context
 * Manages user settings and preferences with admin defaults and overrides
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  UserPreferences,
  PreferencesContextType,
} from '@/types/preferences.types';
import { DEFAULT_PREFERENCES } from '@/types/preferences.types';
import { useAdmin } from './AdminContext';
import { useAuth } from './AuthContext';
import { userPreferencesAPI } from '@/services/userPreferencesService';
import { reportError } from '@/services/errorTelemetry';

const PREFERENCES_KEY = 'stellar_user_preferences';
const PREFERENCES_QUERY_KEY = ['user-preferences'] as const;

const PreferencesContext = createContext<
  PreferencesContextType | undefined
>(undefined);

export function usePreferences(): PreferencesContextType {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error(
      'usePreferences must be used within PreferencesProvider'
    );
  }
  return context;
}

interface PreferencesProviderProps {
  children: ReactNode;
}

/**
 * Read the localStorage-cached preferences merged with admin defaults.
 * Used as React Query placeholderData so cold mounts surface the user's
 * last-known preferences instantly instead of flashing defaults.
 * Returns null when the cache is missing/corrupt.
 */
function readCachedPreferences(
  adminDefaults: Partial<UserPreferences>,
): UserPreferences | null {
  try {
    const saved = localStorage.getItem(PREFERENCES_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved) as Partial<UserPreferences>;
    return { ...DEFAULT_PREFERENCES, ...adminDefaults, ...parsed };
  } catch (error) {
    console.error('[PreferencesContext] Failed to parse cached prefs:', error);
    return null;
  }
}

export function PreferencesProvider({
  children,
}: PreferencesProviderProps) {
  const { config: adminConfig } = useAdmin();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const queryClient = useQueryClient();

  // Memoize so the useMemo below has a stable dep when adminConfig hasn't
  // structurally changed.
  const adminDefaults = useMemo(
    () => adminConfig.userDefaults.defaults || {},
    [adminConfig.userDefaults.defaults],
  );

  // React Query owns the preferences cache. placeholderData seeds the
  // initial render from localStorage so the user's last-known values show
  // instantly; on every successful fetch we mirror the merged value back
  // to localStorage for the next cold load.
  const prefsQuery = useQuery({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: async () => {
      const backendPrefs = await userPreferencesAPI.getPreferences();
      return {
        ...DEFAULT_PREFERENCES,
        ...adminDefaults,
        ...backendPrefs,
      } as UserPreferences;
    },
    enabled: isAuthenticated && !isAuthLoading,
    placeholderData: () => readCachedPreferences(adminDefaults) ?? undefined,
    // Prefs are owner-edited on the same tab; skip refetch-on-focus
    // bursts. updateMutation still invalidates on user edits.
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (prefsQuery.isSuccess && prefsQuery.data) {
      try {
        localStorage.setItem(
          PREFERENCES_KEY,
          JSON.stringify(prefsQuery.data),
        );
      } catch (error) {
        console.error('[PreferencesContext] Failed to cache prefs:', error);
      }
    }
  }, [prefsQuery.isSuccess, prefsQuery.data]);

  // Memoize so the theme/font/a11y effect below doesn't re-fire on every
  // render when the cache miss falls through to the same defaults object.
  const preferences: UserPreferences = useMemo(
    () =>
      prefsQuery.data
      ?? readCachedPreferences(adminDefaults)
      ?? { ...DEFAULT_PREFERENCES, ...adminDefaults },
    [prefsQuery.data, adminDefaults],
  );
  const isLoading = prefsQuery.isFetching;

  // Apply theme/font/a11y settings to <html> whenever preferences change.
  useEffect(() => {
    if (!preferences) return;

    const root = document.documentElement;

    if (preferences.theme === 'dark') {
      root.classList.add('dark');
    } else if (preferences.theme === 'light') {
      root.classList.remove('dark');
    } else {
      const isDark = window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches;
      root.classList.toggle('dark', isDark);
    }

    const fontSizeMap = { small: '14px', medium: '16px', large: '18px' };
    root.style.fontSize = fontSizeMap[preferences.fontSize];

    if (preferences.reducedMotion) {
      root.style.setProperty('--animation-duration', '0.01ms');
    } else {
      root.style.removeProperty('--animation-duration');
    }
    // Also toggle the .reduce-motion class so the CSS selectors in
    // styles/variables.css that key off it (e.g. `.reduce-motion *`) match.
    root.classList.toggle('reduce-motion', preferences.reducedMotion);

    if (preferences.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
  }, [preferences]);

  // Single mutation backing updatePreferences: optimistic apply, backend
  // sync, rollback on failure. Filtering by allowOverride happens in the
  // public updatePreferences wrapper before mutate is called.
  const updateMutation = useMutation({
    mutationFn: (allowedUpdates: Partial<UserPreferences>) =>
      userPreferencesAPI.mergePreferences(allowedUpdates),
    onMutate: async (allowedUpdates) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_QUERY_KEY });
      const previous = queryClient.getQueryData<UserPreferences>(
        PREFERENCES_QUERY_KEY,
      );
      if (previous) {
        queryClient.setQueryData<UserPreferences>(
          PREFERENCES_QUERY_KEY,
          { ...previous, ...allowedUpdates },
        );
      }
      return { previous };
    },
    onError: (error, _vars, context) => {
      console.error('[PreferencesContext] Backend sync failed; keeping local update:', error);
      // We deliberately keep the optimistic update visible even on error
      // so the user's input doesn't disappear (matches the prior
      // fallback-to-local behaviour). The cache already holds the merged
      // optimistic value; only roll back if the optimistic apply itself
      // had nothing to merge against.
      if (!context?.previous) return;
    },
  });

  // Handler intentionally not useCallback-wrapped per the perf-phase1
  // plan; identity changes still propagate via the contextValue useMemo
  // deps array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updatePreferences = async (updates: Partial<UserPreferences>) => {
    // Filter updates based on allowOverride settings
    const allowedUpdates: Partial<UserPreferences> = {};
    const allowOverride = adminConfig.userDefaults.allowOverride;

    for (const key in updates) {
      const prefKey = key as keyof UserPreferences;
      const value = updates[prefKey];
      if (allowOverride[prefKey as keyof typeof allowOverride] !== false) {
        (allowedUpdates as Record<string, unknown>)[prefKey] = value;
      } else {
        console.warn(
          `[PreferencesContext] Preference "${key}" cannot be overridden by user`,
        );
      }
    }

    if (Object.keys(allowedUpdates).length === 0) return;
    await updateMutation.mutateAsync(allowedUpdates).catch(() => {
      // Already logged in onError; the optimistic value stays in place.
    });
  };

  const resetMutation = useMutation({
    mutationFn: () => userPreferencesAPI.resetPreferences(),
    onSuccess: (resetPrefs) => {
      queryClient.setQueryData<UserPreferences>(
        PREFERENCES_QUERY_KEY,
        { ...DEFAULT_PREFERENCES, ...adminDefaults, ...resetPrefs },
      );
    },
    onError: (error) => {
      console.error('[PreferencesContext] Backend reset failed; using local defaults:', error);
      queryClient.setQueryData<UserPreferences>(
        PREFERENCES_QUERY_KEY,
        { ...DEFAULT_PREFERENCES, ...adminDefaults },
      );
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- see updatePreferences
  const resetPreferences = async () => {
    if (
      confirm(
        'Are you sure you want to reset all preferences to default values?',
      )
    ) {
      await resetMutation.mutateAsync().catch(() => {
        // Already handled in onError.
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- see updatePreferences
  const exportPreferences = (): string => {
    return JSON.stringify(
      {
        version: '1.0',
        exportDate: new Date().toISOString(),
        preferences,
      },
      null,
      2,
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- see updatePreferences
  const importPreferences = (data: string) => {
    try {
      const parsed = JSON.parse(data);
      if (parsed.preferences) {
        queryClient.setQueryData<UserPreferences>(
          PREFERENCES_QUERY_KEY,
          { ...DEFAULT_PREFERENCES, ...parsed.preferences },
        );
      } else {
        throw new Error('Invalid preferences format');
      }
    } catch (error) {
      reportError(error, { area: 'preferences-import' }, 'error');
      console.error('Failed to import preferences:', error);
      alert('Failed to import preferences: Invalid format');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- see updatePreferences
  const canOverride = (key: keyof UserPreferences): boolean => {
    const allowOverride = adminConfig.userDefaults.allowOverride;
    return allowOverride[key as keyof typeof allowOverride] !== false;
  };

  const contextValue = useMemo<PreferencesContextType>(
    () => ({
      preferences,
      updatePreferences,
      resetPreferences,
      exportPreferences,
      importPreferences,
      canOverride,
      isLoading,
    }),
    [
      preferences,
      updatePreferences,
      resetPreferences,
      exportPreferences,
      importPreferences,
      canOverride,
      isLoading,
    ],
  );

  return (
    <PreferencesContext.Provider value={contextValue}>
      {children}
    </PreferencesContext.Provider>
  );
}
